import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Music2, Heart, Share2, Link2 } from "lucide-react";
import { fetchSongs, addFavourite, removeFavourite, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PlaylistAdder from "@/components/PlaylistAdder";

const CATEGORIES = ["All", "Praise", "Bible Stories", "Bedtime", "Gratitude", "Movement"];

const toSeconds = (d) => {
  const [m, s] = d.split(":").map(Number);
  return m * 60 + s;
};

const formatPlays = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

const Equalizer = ({ color }) => (
  <span className="flex h-4 items-end gap-[3px]" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="eq-bar w-[3px] rounded-full"
        style={{ height: "100%", backgroundColor: color, animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </span>
);

const Songs = () => {
  const [songs, setSongs] = useState([]);
  const [category, setCategory] = useState("All");
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const audioRef = useRef(null);
  const { user, setUser, openAuth } = useAuth();

  const favourites = new Set(user?.favourites || []);
  const toggleFav = async (songId) => {
    if (!user) {
      openAuth("signup");
      return;
    }
    const updated = favourites.has(songId)
      ? await removeFavourite(songId)
      : await addFavourite(songId);
    setUser({ ...user, favourites: updated });
  };

  const [sharedId, setSharedId] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("song");

  useEffect(() => {
    if (!highlightId || !songs.length) return;
    const el = document.getElementById(`song-card-${highlightId}`);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
  }, [highlightId, songs]);

  const shareSong = async (song) => {
    const url = `${window.location.origin}/sing?song=${song.song_id}`;
    const text = `Sing "${song.title}" with us — free Christian songs for little ones at Little Songs. Big Faith.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: song.title, text, url });
        return;
      } catch { /* share cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch { /* clipboard blocked */ }
    setSharedId(song.song_id);
    setTimeout(() => setSharedId(null), 2200);
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  useEffect(() => () => stopAudio(), []);

  useEffect(() => {
    fetchSongs().then(setSongs).catch(() => {});
  }, []);

  useEffect(() => {
    if (!playingId) return;
    const song = songs.find((s) => s.song_id === playingId);
    if (!song || song.audio_url) return; // real audio drives its own progress
    const total = toSeconds(song.duration);
    timer.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setPlayingId(null);
          return 0;
        }
        return p + 100 / (total * 4);
      });
    }, 250);
    return () => clearInterval(timer.current);
  }, [playingId, songs]);

  const toggle = (song) => {
    if (playingId === song.song_id) {
      stopAudio();
      setPlayingId(null);
      return;
    }
    stopAudio();
    setPlayingId(song.song_id);
    setProgress(0);
    if (song.audio_url) {
      const a = new Audio(resolveImage(song.audio_url));
      audioRef.current = a;
      a.ontimeupdate = () => a.duration && setProgress((a.currentTime / a.duration) * 100);
      a.onended = () => {
        setPlayingId(null);
        setProgress(0);
      };
      a.play().catch(() => {});
    }
  };

  const visible = useMemo(
    () => (category === "All" ? songs : songs.filter((s) => s.category === category)),
    [songs, category]
  );

  return (
    <section id="sing" className="relative flex min-h-[100svh] flex-col justify-center bg-[#F8F5EC] py-28 sm:py-36" data-testid="songs-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs font-bold uppercase tracking-[0.3em] text-[#0284C7]"
            >
              Sing
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 }}
              className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
            >
              Songs they&apos;ll ask for again. <span className="font-hand text-[#FF6B6B]">And again.</span>
            </motion.h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="song-category-filter">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`song-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  category === c
                    ? "bg-slate-900 text-white shadow-lg scale-105"
                    : "bg-white text-slate-600 border border-[#E2DDD0] hover:border-slate-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((song) => {
              const isPlaying = playingId === song.song_id;
              return (
                <motion.article
                  layout
                  key={song.song_id}
                  id={`song-card-${song.song_id}`}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  transition={{ duration: 0.35 }}
                  className={`group relative overflow-hidden rounded-3xl border bg-white p-6 transition-shadow ${
                    isPlaying
                      ? "border-transparent shadow-[0_20px_40px_-10px_rgba(14,165,233,0.3)] ring-2"
                      : "border-[#E2DDD0] shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)] hover:shadow-[0_14px_28px_rgba(0,0,0,0.06)]"
                  }${highlightId === song.song_id && !isPlaying ? " ring-2 ring-[#0284C7]" : ""}`}
                  style={isPlaying ? { "--tw-ring-color": song.color } : {}}
                  data-testid={`song-card-${song.song_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => toggle(song)}
                      data-testid={`song-play-${song.song_id}`}
                      aria-label={isPlaying ? `Pause ${song.title}` : `Play ${song.title}`}
                      className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-white shadow-lg transition-transform hover:scale-110 active:scale-95"
                      style={{ backgroundColor: song.color }}
                    >
                      {isPlaying ? <Pause className="h-6 w-6 fill-white" /> : <Play className="ml-0.5 h-6 w-6 fill-white" />}
                    </button>
                    <span className="flex items-center gap-2">
                      <motion.button
                        whileTap={{ scale: 0.75 }}
                        onClick={() => toggleFav(song.song_id)}
                        data-testid={`song-fav-${song.song_id}`}
                        aria-label={`Save ${song.title} to favourites`}
                        className="grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-[#FFE5E5]"
                      >
                        <Heart
                          className={`h-4.5 w-4.5 h-[18px] w-[18px] transition-colors ${
                            favourites.has(song.song_id)
                              ? "fill-[#FF6B6B] text-[#FF6B6B]"
                              : "text-slate-300"
                          }`}
                        />
                      </motion.button>
                      <PlaylistAdder song={song} />
                      <span className="relative">
                        <button
                          onClick={() => shareSong(song)}
                          data-testid={`song-share-${song.song_id}`}
                          aria-label={`Share ${song.title}`}
                          className="grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-[#FEF3C7]"
                        >
                          {sharedId === song.song_id ? (
                            <Link2 className="h-[18px] w-[18px] text-[#16A34A]" />
                          ) : (
                            <Share2 className="h-[18px] w-[18px] text-slate-300 transition-colors hover:text-slate-500" />
                          )}
                        </button>
                        {sharedId === song.song_id && (
                          <span
                            className="absolute -top-8 right-0 whitespace-nowrap rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white"
                            data-testid={`song-share-copied-${song.song_id}`}
                          >
                            Link copied
                          </span>
                        )}
                      </span>
                      {isPlaying ? (
                        <Equalizer color={song.color} />
                      ) : (
                        <Music2 className="h-5 w-5 text-slate-300 transition-colors group-hover:text-slate-400" />
                      )}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-slate-900">{song.title}</h3>
                  <p className="mt-1 text-sm font-semibold" style={{ color: song.color }}>
                    {song.category} &bull; {song.verse}
                  </p>
                  <p className="mt-3 font-hand text-xl leading-snug text-slate-500">&ldquo;{song.lyric}&rdquo;</p>
                  <div className="mt-4 flex items-center justify-between text-xs font-semibold text-slate-400">
                    <span>{formatPlays(song.plays)} plays</span>
                    <span>{song.duration}</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F8F5EC]">
                    <div
                      className="h-full rounded-full transition-[width] duration-200"
                      style={{ width: isPlaying ? `${progress}%` : "0%", backgroundColor: song.color }}
                    />
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Songs;
